<template>
    <div :class="['pt-table-row_cell', column.key]" :style="cellStyle">
        <slot>{{ source[column.key] ?? '--' }}</slot>
    </div>
</template>

<script lang="ts">
export default {
    name: 'RowCell',
};
</script>

<script lang="ts" setup>
import type { Props as TableProps } from '../table.vue';
import { computed } from 'vue';
import Color from 'color';
import { cloneDeep } from 'lodash-es';

interface Props {
    source: any;
    column: TableProps['columns'][number];
    columnWidth?: string;
}
const props = defineProps<Props>();
const defaultColors = ['#FFBEB5', '#FFFFFF', '#B0E3B2']; // 红白绿
const defaultColors2 = ['#B0E3B2', '#FFFFFF', '#FFBEB5']; // 绿白红
const defaultPositiveColors = ['#FFFFFF', '#B0E3B2']; // 白绿
const defaultNegativeColors = ['#FFBEB5', '#FFFFFF']; // 红白
const defaultProportionColor = '#f4f5f7';
const rankColorMap = {
    a: '#B0E3B2',
    b: '#FFFFFF',
    c: '#FFFFFF',
    d: '#FFFFFF',
    e: '#FFBEB5',
};
const cellStyle = computed(() => {
    const styleProperties: {
        width: string;
        background?: string;
    } = {
        width: props.column?.width || (props.columnWidth as string),
    };
    const { min, max, colorType, currentColumnRange, negative, positive, colorReverse } = props.column;
    if (!colorType) return styleProperties;
    const currentValue = props.source[props.column.colorKey];

    if (isNaN(currentValue) || currentValue === undefined) return styleProperties;

    if (colorType === 'RANK') {
        styleProperties.background = rankColorMap[currentValue] || '#FFFFFF';
    } else if (colorType === 'PROPORTION' && typeof max === 'number') {
        const proportion = max !== 0 && currentValue !== 0 ? `${((currentValue / max) * 100).toFixed(2)}%` : 0;
        styleProperties.background = `linear-gradient(90deg, ${defaultProportionColor} 0 ${proportion}, transparent ${proportion} 100%)`;
    } else if (
        colorType === 'SCALE' &&
        typeof max === 'number' &&
        typeof min === 'number' &&
        typeof currentValue === 'number' &&
        min !== max &&
        currentColumnRange?.length
    ) {
        if (!negative || !positive) {
            styleProperties.background = getColor(
                currentValue,
                min,
                max,
                colorReverse ? defaultColors2 : defaultColors,
            );
        } else {
            const { max: negativeMax, min: negativeMin, values: negativeValues } = negative;
            const { max: positiveMax, min: positiveMin, values: positiveValues } = positive;

            if (currentValue <= negativeMax) {
                // 在低值区
                if (negativeMin === 0 && negativeMax === 0) {
                    styleProperties.background = colorReverse ? defaultPositiveColors[1] : defaultNegativeColors[0];
                } else {
                    styleProperties.background = getColor(
                        currentValue,
                        negativeMin,
                        negativeMax,
                        colorReverse ? cloneDeep(defaultPositiveColors).reverse() : defaultNegativeColors,
                    );
                }
            } else if (currentValue >= positiveMin) {
                // 在高值区
                if (positiveMax === positiveMin) {
                    styleProperties.background = colorReverse ? defaultNegativeColors[0] : defaultPositiveColors[1];
                } else {
                    styleProperties.background = getColor(
                        currentValue,
                        positiveMin,
                        positiveMax,
                        colorReverse ? cloneDeep(defaultNegativeColors).reverse() : defaultPositiveColors,
                    );
                }
            } else {
                //  中间50%区域的值
                styleProperties.background = '#FFFFFF';
            }
        }
    }

    return styleProperties;
});

function getColor(value: number, min: number, max: number, colors: string[]) {
    try {
        if (value <= min) return colors[0];
        if (value >= max) return colors[colors.length - 1];

        // 将 hex 颜色值转换为 rgb 格式
        colors = colors.map((c) => Color(c).object());
        const colorsLen = colors.length;

        // 计算当前值在最大最小值中的位置
        const rang = max - min;
        let weight = (value - min) / rang;

        // 计算颜色列表的最后一个索引值
        const endIndex = Math.max(Math.ceil((colorsLen - 1) * weight), 1);
        // 获取两个颜色最小颜色（起始色）、最大颜色（结束色）
        const minColor = colors[endIndex - 1];
        const maxColor = colors[endIndex];

        // 计算色阶比例
        weight = weight * (colorsLen - 1) - (endIndex - 1);

        // 线性插值公式
        const r = (1 - weight) * minColor.r + weight * maxColor.r;
        const g = (1 - weight) * minColor.g + weight * maxColor.g;
        const b = (1 - weight) * minColor.b + weight * maxColor.b;

        return `rgb(${r},${g},${b})`;
    } catch (e) {
        console.log(e, value, min, max);
        return 'rgb(255, 255, 255)';
    }
}
</script>

<style lang="scss">
.pt-table-row_cell {
    display: inline-block;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    flex-grow: 0;
    flex-shrink: 0;
    padding-left: 24px;
    position: relative;
}
</style>
